// LIVE-1's CLIENT HALF. The server half shipped at ffc37676 and nothing on a screen called it.
//
// >>> **HER RULING, 2026-08-26: GOING LIVE IS ONE TAP, LIKE INSTAGRAM LIVE.** <<<
//
// The first build of this file handed a member an RTMPS address and a stream key with five steps
// about OBS beside them. She rejected that outright and she is right: that is a PRODUCTION
// workflow, and a member who has to install software and copy a secret has not gone live, she has
// been given homework. Tap GO LIVE, allow the camera, see yourself, confirm, you are on air.
//
// The infrastructure does not change -- it is the same Cloudflare live input, the same session row,
// the same heartbeat and the same kill switch. What changes is WHO holds the camera: the browser
// publishes straight to Cloudflare's WHIP endpoint (`webRTC.url`, verified on the wire rather than
// remembered), so there is nothing to install, nothing to copy and no key on her screen.
//
// The RTMP screen survives as an ADVANCED door on the owner's own seat, because she may want OBS
// production quality one day. It is never drawn for a member, and the server no longer sends the
// key to one.
import { safeLocalStorage, safeSessionStorage } from "../lib/safe-storage";
import { APP_MODE } from "../lib/appMode";
import { cameraRefusal } from "../lib/camera-ask";
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { LIVE_FILTERS, activeFilter, readFilterPrefs, writeFilterPrefs, STRENGTH_MIN, STRENGTH_MAX } from "../liveFilters.js";
import { startFilterPipeline, type FilterPipeline, type FilterTier } from "./liveFilterPipeline";
import { startFaceTracker } from "./liveFaceMask";
import { liveToOpen, writeLiveIntent, readLiveIntent, clearLiveIntent, addressWithoutLive } from "../liveLink.js";
import { livePresenceAction, tracksShouldSend, showsPausedCard, LIVE_PRESENCE, LIVE_AWAY_LIMIT_MS } from "../livePresence.js";
import { Radio, X, CircleDot, SwitchCamera, Loader, Eye, Sparkles, Send, Volume2, Flag } from "lucide-react";
// ITEM 4(c)'s ONE component that draws a member's face. Her 2026-08-28 rulings 3 and 4 are both
// "use it here": the live lane and the live circle were the two surfaces that never asked.
import { openMemberMenu, Avatar } from "./LivingRoomShared";
import {
  DEFAULT_LIVE_SCOPE, LIVE_HEARTBEAT_MS, LIVE_SCOPES, LIVE_SCOPE_LABELS,
  liveScopeOf, scopeNeedsGroup, sessionIsWatchable,
} from "../liveRoom.js";
// HER GO-LIVE SAFETY CHECK (2026-09-13). The DECISION is the law's, in `src/goLiveGuard.js`, where a
// fixture RUNS it against the exact failure of that day; this screen only draws the answer.
import { goLiveGuardFor, goLiveButtonLabel, GATE_DROP_PREP_MINUTES } from "../goLiveGuard.js";

export type LiveSession = {
  id: string;
  userId?: string;
  title?: string;
  scope?: string;
  startedAt?: string;
  lastSeenAt?: string;
  endedAt?: string | null;
  authorName?: string;
  // Her ruling 4: the circle wears the broadcaster's own face, resolved server-side like every
  // other surface in the house.
  photo?: string | null;
  mine?: boolean;
};

const json = (url: string, init?: RequestInit) =>
  fetch(url, init).then((r) => r.json().catch(() => null)).catch(() => null);
const post = (url: string, body: unknown) =>
  json(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

const hostLabel = (s: LiveSession) => s.authorName || s.title || "Live";
const initialOf = (s: LiveSession) => (hostLabel(s).trim()[0] || "L").toUpperCase();

// A phone is the only place a camera flip means anything, and it is asked of the DEVICE rather than
// of the user agent string -- a sniffed browser name is a guess about a capability the device will
// answer for itself.
async function hasTwoCameras(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === "videoinput").length > 1;
  } catch {
    return false;
  }
}

/**
 * >>> PUBLISHING FROM THE BROWSER: WHIP, AND WHY IT WAITS FOR ICE. <<<
 *
 * WHIP is one POST: the SDP offer goes up as `application/sdp` and the answer comes back in the
 * body. Cloudflare's endpoint is NOT a trickle-ICE endpoint, so an offer sent before gathering has
 * finished describes candidates the server will never hear about -- the connection then fails
 * silently, which is the worst shape this could take on the surface a member is standing on. So the
 * offer is held until gathering completes, with a ceiling: a browser behind a firewall can gather
 * for ever, and a host waiting for ever with no sentence is the same silence by another road.
 */
/**
 * >>> **HER RULING, 2026-09-18, AFTER @freddie's LIVE: SOUND IS AUTOMATIC, NOT A BUTTON.** <<<
 *
 * *"I want everybody sound to be automatic when they go Live, not a button they can tap."*
 *
 * **WHAT ACTUALLY HAPPENED, AND I GOT IT WRONG TWICE BEFORE I GOT IT RIGHT.** His stream was fine
 * and his audio was fine — she turned the sound on with the button and heard him. The defect was
 * never the stream: **every viewer starts muted, and the one control that fixes it is a small button
 * over a full-bleed video.**
 *
 * **AND THE BUTTON ALREADY SAID THE RIGHT WORDS — `Tap for sound`, exactly what she went looking
 * for — AND SHE STILL COULD NOT FIND IT.** That is the whole finding. It is not a wording defect
 * and no better wording fixes it: it is a small control on a full-screen picture, and the owner of
 * the house missed it while actively hunting for it. At 3,751 called members, a room that cannot
 * find it reads as "nobody can hear him", and they are right to read it that way.
 *
 * -------------------------------------------------------------------------------------------------
 * >>> **WHY IT STARTED MUTED AT ALL, WHICH IS NOT A MISTAKE.** <<<
 *
 * iOS and Android refuse `play()` on an unmuted element with no user gesture. An earlier round paid
 * for that with a black screen: the promise rejected and the picture never began. **Muted autoplay is
 * the platform's contract and the picture must never be what fails.**
 *
 * -------------------------------------------------------------------------------------------------
 * >>> **SO: PICTURE FIRST, THEN SOUND, THEN THE VIEWER'S VERY NEXT TOUCH — ANYWHERE.** <<<
 *
 * 1. Muted `play()`, which no browser refuses. The picture is alive.
 * 2. Immediately try unmuting. **She tapped a live card to get into this room**, which is a user
 *    activation in every browser that has the concept, so this usually simply works.
 * 3. If the browser still refuses, arm a ONE-TIME listener for her next touch, tap, scroll or key —
 *    **anywhere on the screen, not on a button she has to find.** The first time she does anything
 *    at all, the sound comes on.
 *
 * **THAT IS THE "AUTOMATIC" HER RULING ASKS FOR, AS FAR AS A BROWSER PERMITS.** No browser will ever
 * let a page make noise with no interaction whatsoever — that is the platform's rule and not a choice
 * this house gets to make. What it will allow is sound the moment she touches anything, and that is
 * now what happens.
 */
function autoSound(el: HTMLVideoElement, onMuted: (m: boolean) => void): () => void {
  let done = false;
  const EVENTS = ["pointerdown", "touchstart", "keydown", "wheel"] as const;

  const unmute = async () => {
    if (done || !el.isConnected) return;
    try {
      el.muted = false;
      await el.play();
      // >>> **A RESOLVED `play()` IS NOT PROOF OF SOUND, AND SAFARI IS WHY.** <<< iOS does not always
      // reject: on an element already playing it will resolve the promise and quietly PAUSE, or leave
      // `muted` back on. Trusting the promise alone would report sound on the one device her audience
      // is actually holding and deliver silence (R53/R136/R137 — read the state, never the shape).
      if (el.muted || el.paused) throw new Error("the browser took the sound back");
      done = true;
      onMuted(false);
      off();
    } catch {
      // Refused. Straight back to the picture, which must never be what fails.
      el.muted = true;
      onMuted(true);
      void el.play().catch(() => {});
    }
  };

  const onGesture = () => { void unmute(); };
  const off = () => { for (const e of EVENTS) document.removeEventListener(e, onGesture, true); };

  // The attempt, now. Then the net, armed for her next touch of anything at all.
  void unmute().then(() => {
    if (!done) for (const e of EVENTS) document.addEventListener(e, onGesture, true);
  });

  return off;
}

async function publishToWhip(url: string, stream: MediaStream, ice?: RTCIceServer[]): Promise<RTCPeerConnection> {
  // >>> **THE RELAY COMES FROM THE SERVER, AND STUN-ONLY IS THE FALLBACK.** <<< A network that
  // refuses UDP to Cloudflares one entry has no way through without TURN, which is her event of
  // 2026-09-13 in one sentence. The list is minted server side so the API token never reaches a
  // browser, and an empty or missing list lands on exactly what shipped before it (R19).
  const pc = new RTCPeerConnection({
    iceServers: (Array.isArray(ice) && ice.length) ? ice : [{ urls: "stun:stun.cloudflare.com:3478" }],
  });
  for (const track of stream.getTracks()) pc.addTrack(track, stream);

  // >>> **WHAT THE BROWSER ACTUALLY GATHERED, KEPT SO A FAILURE CAN SAY WHY.** <<< A publish that
  // offers only `host` candidates cannot be reached by a server-side agent, because modern browsers
  // hide host addresses behind mDNS `.local` names that nothing outside the LAN can resolve. `srflx`
  // is the one that matters. Counting them costs nothing and turns "it did not connect" into a fact.
  const gathered: string[] = [];
  pc.addEventListener("icecandidate", (e) => {
    const c = e.candidate?.candidate || "";
    const m = /\btyp\s+(\w+)/.exec(c);
    if (m) gathered.push(m[1]);
  });

  await pc.setLocalDescription(await pc.createOffer());

  if (pc.iceGatheringState !== "complete") {
    await new Promise<void>((resolve) => {
      const done = () => { clearTimeout(timer); pc.removeEventListener("icegatheringstatechange", onChange); resolve(); };
      const onChange = () => { if (pc.iceGatheringState === "complete") done(); };
      const timer = setTimeout(done, 4000);
      pc.addEventListener("icegatheringstatechange", onChange);
    });
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/sdp" },
    body: pc.localDescription?.sdp || "",
  });
  // R39/R47: read the answer, never the status alone. An SDP body is what makes this a connection.
  const answer = await res.text().catch(() => "");
  if (!res.ok || !answer.includes("v=0")) {
    pc.close();
    throw new Error("The broadcast could not connect just now.");
  }
  await pc.setRemoteDescription({ type: "answer", sdp: answer });

  // =================================================================================================
  // >>> **AND THEN WE WAIT TO SEE IF IT ACTUALLY CONNECTED, WHICH NOTHING HERE EVER DID.** <<<
  //
  // **MEASURED ON HER OWN EVENT, 2026-09-13.** Five broadcasts, two browsers, one evening. Cloudflare
  // recorded `state: "disconnected"`, `history: []`, zero videos on every single input: not one frame
  // ever arrived. The SDP exchange succeeded every time, so this function returned happily, the panel
  // said LIVE, the comments scrolled, viewers arrived, and the ONE thing that had failed was the one
  // thing nobody was watching. She performed to an empty room for an hour.
  //
  // **THE HANDSHAKE IS NOT THE CONNECTION.** The offer and answer go over HTTPS and will succeed on
  // any network that can load the site at all. The MEDIA goes over UDP to whatever candidate
  // Cloudflare hands back (measured: a host candidate on port 1473), and a network that blocks that
  // port fails here, silently, with no TURN relay configured to fall back to.
  //
  // So the promise is not resolved until the peer connection says `connected`. A failure throws, and
  // the caller already does the right thing with a throw: it ends the session rather than leaving a
  // broadcast nobody is in. **An honest refusal she can act on beats a live broadcast that is a lie.**
  // =================================================================================================
  await new Promise<void>((resolve, reject) => {
    if (pc.connectionState === "connected") { resolve(); return; }
    const settle = (fn: () => void) => {
      clearTimeout(timer);
      pc.removeEventListener("connectionstatechange", onChange);
      fn();
    };
    const onChange = () => {
      if (pc.connectionState === "connected") settle(resolve);
      // `failed` is final. `disconnected` is not, and treating it as final would end a broadcast
      // over a two-second wobble on a phone (R18: two states, two answers).
      else if (pc.connectionState === "failed") {
        settle(() => reject(new Error(whyItFailed(gathered, pc))));
      }
    };
    const timer = setTimeout(() => {
      settle(() => {
        if (pc.connectionState === "connected") { resolve(); return; }
        reject(new Error(whyItFailed(gathered, pc)));
      });
    }, WHIP_CONNECT_MS);
    pc.addEventListener("connectionstatechange", onChange);
  }).catch((e) => { pc.close(); throw e; });

  return pc;
}

/** How long to give the media path before calling it. Long enough for a slow phone on a good
 *  network, short enough that she is not talking to nobody for a minute. */
const WHIP_CONNECT_MS = 12_000;

/**
 * >>> **THE SENTENCE SHE READS WHEN THE VIDEO DOES NOT GET OUT, AND IT NAMES THE EVIDENCE.** <<<
 *
 * Written on 2026-09-13, after five broadcasts to an empty room across two browsers and two
 * networks, because *"it did not connect"* is not something anybody can act on. **`srflx` is the
 * discriminator.** A browser that gathered one knows its own public address and the failure is
 * downstream; a browser that gathered only `host` is offering `.local` names no server can resolve,
 * and that is a STUN problem rather than a firewall one. The two have different repairs, so they
 * get different sentences (R18) and the counts ride along either way.
 */
function whyItFailed(gathered: string[], pc: RTCPeerConnection): string {
  const kinds = [...new Set(gathered)];
  const srflx = gathered.filter((k) => k === "srflx").length;
  const detail = `[${pc.connectionState}/${pc.iceConnectionState}; ${gathered.length} candidates: ${kinds.join(", ") || "none"}]`;
  if (!gathered.length) {
    return `Your video could not get out. ${APP_MODE ? "Your phone" : "The browser"} found no way to connect at all. ${detail}`;
  }
  if (!srflx) {
    return `Your video could not get out. ${APP_MODE ? "Your phone" : "The browser"} never learned its own public address, so we could not be reached. ${detail}`;
  }
  return `Your video could not get out. The connection was refused on the way. Try a different network, or your phone's data. ${detail}`;
}

/**
 * >>> HER RULING: A LIVE IS A ROOM, NOT A CAMERA. <<<
 *
 * Presence, comments and the count, polled on the broadcast's OWN heartbeat so the room and the
 * session agree about what "still here" means (R28/R29). One poll answers all three, because three
 * timers against one server is three chances for the count and the comments to disagree.
 *
 * The JOIN LINE is raised by the SERVER's answer, not by this component noticing a new name: a
 * reconnect on a flaky phone would otherwise announce her again and again, which reads to the room
 * as somebody pacing in and out. The stamp is claimed once, in the statement that writes it.
 */
function useLiveRoom(sessionId: string, active: boolean) {
  const [viewers, setViewers] = useState(0);
  const [comments, setComments] = useState<LiveComment[]>([]);
  const [who, setWho] = useState<{ userId: number; name: string }[] | null>(null);
  const [joins, setJoins] = useState<{ id: string; name: string }[]>([]);
  // >>> **HER 2026-08-28 CLOSURE LAW: THE VIEWER LEARNS, WITHIN SECONDS.** <<< Her test was a
  // broadcaster ending and an audience left in a dead room. The SERVER was already right about it --
  // `roomSession` refuses a session that is not watchable -- and this poll, which runs every three
  // seconds and already knew, threw the answer away. One door, one truth: the same read that carries
  // her comments carries the ending.
  const [over, setOver] = useState(false);
  const sinceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !sessionId) return undefined;
    let alive = true;

    const announce = (name: string) => {
      const id = `${name}:${sinceRef.current || "0"}:${Math.round(performance.now())}`;
      setJoins((list) => [...list, { id, name }]);
      // Her line RISES AND FADES; it is not a log. Removed by the same clock that drew it.
      window.setTimeout(() => { if (alive) setJoins((list) => list.filter((j) => j.id !== id)); }, 4200);
    };

    (async () => {
      const joined = await post("/api/live/join", { sessionId });
      if (!alive || !joined?.ok) return;
    })();

    const read = async () => {
      const url = `/api/live/room/${encodeURIComponent(sessionId)}${sinceRef.current ? `?since=${sinceRef.current}` : ""}`;
      const d = await json(url);
      if (!alive) return;
      // R39/R47: READ THE PAYLOAD. A refusal here is not a hiccup to shrug at -- it is the broadcast
      // being over, which is the one thing the person watching needs to be told.
      if (d && d.ok === false) { setOver(true); return; }
      if (!d?.ok) return;
      setViewers(Number(d.viewers || 0));
      if (Array.isArray(d.who)) setWho(d.who);
      if (Array.isArray(d.comments) && d.comments.length) {
        sinceRef.current = d.comments[d.comments.length - 1].id;
        // The floor keeps the strip short: a live comment is a thing that goes by, not a transcript.
        setComments((list) => [...list, ...d.comments].slice(-40));
      }
      // >>> **HER MODERATION RULING: THE LANE DROPS AS WELL AS ADDS.** <<< A cursor-fed lane can only
      // ever grow, which is exactly why a comment the broadcaster deleted stayed on every other
      // screen. The server says what is still visible; anything not in that answer leaves -- which
      // carries her block clause too, since a member blocked mid-live stops being in it.
      //
      // **PRESENCE, NEVER TRUTHINESS (R53/R136/R137):** only a real ARRAY prunes. A payload without
      // the field must never empty somebody's lane, which is the one failure this could cause.
      if (Array.isArray(d.visible)) {
        const keep = new Set(d.visible.map(String));
        setComments((list) => list.filter((x) => keep.has(String(x.id))));
      }
    };
    read();
    const t = window.setInterval(read, 3000);
    return () => {
      alive = false;
      window.clearInterval(t);
      post("/api/live/leave", { sessionId });
    };
  }, [sessionId, active]);

  return { viewers, comments, who, joins, over, announceJoin: (n: string) => n };
}

// Her 2026-08-28 ruling 3: the face rides the row. `photo` is a URL the server decided this viewer
// may see, or null — the client never reasons about visibility, it renders what it was handed.
export type LiveComment = { id: string; userId: number; body: string; at: string; name: string; photo?: string | null; avatar?: string };

// THE WATCH SCREEN. The playback address is READ off the server's answer, never composed.
/**
 * >>> **HER FIELD REPORT, 2026-08-26: THE GO-LIVE BUTTON WAS UNREACHABLE ON A PHONE, AND THE REASON
 * IS A STACKING CONTEXT RATHER THAN A LAYOUT.** <<<
 *
 * MEASURED at 390x844 before a line was written (R25/R180). Her "Go live now" control sits at
 * y=783..827 and the mobile tab bar's top is y=763, and the hit test at the button's own centre
 * came back **`mobile-tab`** — her tap landed on the bottom navigation, not on her broadcast.
 * Cancel, beside it, hit-tested to an icon inside the same bar.
 *
 * **AND THE Z-INDEX WAS ALREADY RIGHT, WHICH IS THE WHOLE POINT.** The sheet computes to `z-index:
 * 910` against the dock's `120` — but its parent is `.live-rail`, nested inside `main`, so its 910
 * is scoped to an ancestor's stacking context and can never rise above a bar portalled to
 * `<body>`. A z-index raised here would have read as a fix in every diff and changed nothing.
 *
 * **SO THE SURFACE MOVES TO `<body>`, WHICH IS L4's OWN REPAIR ON THE OTHER HALF OF THIS COLLISION**
 * (*"THE BAR IS PORTALLED TO `<body>`"*) — the same fix, for the same reason, on the surface it
 * collides with. It is ONE helper because `.live-watch` is trapped in the identical way: the
 * viewer's whole overlay lives inside `.live-rail` too, which puts her comment box under the dock.
 *
 * **SSR-SAFE, because a missing `document` is a render and not a crash** (R19): the node is
 * returned in place rather than thrown away, so nothing that renders this off a browser goes blank.
 */
function OverDock({ children }: { children: ReactNode }) {
  if (typeof document === "undefined") return <>{children}</>;
  return createPortal(children, document.body);
}

export function LiveWatch({ session, onClose }: { session: LiveSession; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [why, setWhy] = useState("");
  const [note, setNote] = useState("");
  // >>> **HER 2026-08-28 FIRE: THE AUDIENCE COULD NOT HEAR HER.** <<< Her microphone was never the
  // problem -- `getUserMedia` asks for `audio: true` and the WHIP publish adds EVERY track on the
  // stream, so her voice has been on the wire the whole time. The player is what was silent: a
  // `<video>` element cannot autoplay with sound on a phone, so it starts MUTED by platform law,
  // and nothing on this screen ever turned it back on. The only way out was the browser's own
  // control bar, on a full-bleed video where it is all but unreachable.
  const [muted, setMuted] = useState(true);
  // >>> **HER CLOSURE LAW (b): NEVER A FROZEN FRAME OR AN INFINITE SPINNER.** <<< Her test was a
  // broadcaster ending and an audience left in a dead room. This is the warm beat before they are
  // handed back to the Living Room.
  const [ended, setEnded] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);

  // 2.6s is a BEAT rather than a timeout: long enough to read her sentence, short enough that nobody
  // is held on a dead screen. The close is the same `onClose` the X uses -- one exit, so a viewer
  // always lands back where she came from (R28/R29).
  useEffect(() => {
    if (!ended) return undefined;
    const t = window.setTimeout(() => onClose(), 2600);
    return () => window.clearTimeout(t);
  }, [ended, onClose]);

  useEffect(() => {
    let alive = true;
    // HER RULING 2026-09-18 — the any-touch net is disarmed with the rest of the viewer.
    let stopSound: () => void = () => {};
    let hls: { destroy: () => void } | null = null;
    let peer: RTCPeerConnection | null = null;
    (async () => {
      // >>> **HER FIELD REPORT: VIEWERS SEE A BROKEN ICON AND NOT VIDEO — AND THIS ASKED ONCE.** <<<
      //
      // Cloudflare does not have a playback URL the instant she goes on air; it has one about ten to
      // twenty seconds later. This read fired ONCE on mount, and when the answer carried no `hls`
      // it set a note and **never asked again** — so a viewer who tapped in early sat in front of a
      // `<video>` with no source for the whole broadcast. An empty video element with no poster is
      // exactly the broken-media placeholder she is describing.
      //
      // **SO IT POLLS UNTIL THE STREAM IS REALLY THERE**, on the same heartbeat the rail already
      // uses (R28/R29 — one cadence in this file, not a second number invented here), and the
      // walk-away is a CHECKED CLAUSE rather than a clock: it stops on the first playable URL, or
      // when the broadcast is really over.
      let src = "";
      let whep = "";
      // The relay list rides the same answer the playback URL does, so the viewer never asks twice.
      let iceList: RTCIceServer[] | null = null;
      for (let tries = 0; alive && tries < 40; tries += 1) {
        const d = await json(`/api/live/watch/${encodeURIComponent(session.id)}`);
        if (!alive) return;
        if (!d?.ok) { setNote(d?.message || "That broadcast is not live."); return; }
        src = d?.playback?.hls || "";
        whep = d?.playback?.whep || "";
        iceList = Array.isArray(d?.ice) && d.ice.length ? (d.ice as RTCIceServer[]) : null;
        if (src || whep) break;
        setWhy(d?.playbackWhy || "starting");
        await new Promise((r) => setTimeout(r, 2000));
      }
      // >>> **AND A VIEWER WITH WHEP AND NO MANIFEST IS NOT A VIEWER WITH NOTHING.** <<< This read
      // `!src`, so a WebRTC broadcast whose HLS ladder had not been packaged yet — which is every
      // broadcast for its first tens of seconds — returned here and showed a black screen, with the
      // WHEP path sitting unused three lines below. The loop above already breaks on EITHER, so this
      // was refusing exactly what that loop had just accepted (R28/R29).
      if (!alive || (!src && !whep)) return;
      setWhy("");
      const el = videoRef.current;
      if (!el) return;
      // >>> **WHEP FIRST, BECAUSE IT IS THE RECEIVE HALF OF WHAT SHE IS PUBLISHING.** <<< She goes on
      // air over WHIP; the HLS manifest beside it is the transcoded ladder and it lags her by tens of
      // seconds, which is why her audience met a black screen while the room and the comments worked.
      // This is the same handshake the broadcaster already does, in the other direction: an offer that
      // only RECEIVES, posted as SDP, and the answer set straight back.
      if (whep) {
        // The viewer needs the relay for the same reason the publisher does: WHEP is WebRTC in the
        // other direction, and a viewer on the same kind of network meets the same black screen.
        const pc = new RTCPeerConnection(
          (Array.isArray(iceList) && iceList.length) ? { iceServers: iceList } : undefined,
        );
        peer = pc;
        pc.addTransceiver("video", { direction: "recvonly" });
        pc.addTransceiver("audio", { direction: "recvonly" });
        const remote = new MediaStream();
        pc.ontrack = (ev) => {
          remote.addTrack(ev.track);
          // PRESENCE, from the connection itself: her voice is offered only when a real audio track
          // arrived, which is the one thing that tells a silent stream from a muted player (R18).
          if (ev.track.kind === "audio") setHasAudio(true);
          el.srcObject = remote;
          // The picture first, always: a muted play is the one the platform never refuses.
          el.play().catch(() => {});
          // >>> **THEN SOUND, BECAUSE SHE TAPPED TO GET HERE (2026-09-18).** <<< Her emergency was a
          // room of people watching a silent broadcast with the tap-for-sound button on screen. The
          // button stays for every browser that refuses; this is for every browser that does not.
          if (ev.track.kind === "audio") { stopSound(); stopSound = autoSound(el, setMuted); }
        };
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const answer = await fetch(whep, {
          method: "POST", headers: { "content-type": "application/sdp" }, body: offer.sdp || "",
        }).then((r) => (r.ok ? r.text() : "")).catch(() => "");
        if (!alive) return;
        // **A WHEP THAT REFUSES FALLS THROUGH TO THE MANIFEST RATHER THAN ENDING THE ATTEMPT** — the
        // failure this round exists to stop is a viewer with nothing at all (R19).
        if (answer) {
          await pc.setRemoteDescription({ type: "answer", sdp: answer }).catch(() => {});
          return;
        }
        pc.close();
        peer = null;
      }
      if (!src) return;
      setHasAudio(true);
      if (el.canPlayType("application/vnd.apple.mpegurl")) {
        el.src = src;
        el.play().catch(() => {});
        // Native HLS carries its audio in the same element, so the same attempt applies.
        stopSound();
        stopSound = autoSound(el, setMuted);
        return;
      }
      const mod = await import("hls.js");
      if (!alive) return;
      const Hls = mod.default;
      if (!Hls.isSupported()) { setNote(APP_MODE ? "This phone cannot play a live stream." : "This browser cannot play a live stream."); return; }
      const player = new Hls({ lowLatencyMode: true });
      hls = player;
      player.loadSource(src);
      player.attachMedia(el);
      el.play().catch(() => {});
      stopSound();
      stopSound = autoSound(el, setMuted);
    })();
    return () => { alive = false; stopSound(); hls?.destroy(); peer?.close(); };
  }, [session.id]);

  return (
    <OverDock>
    <div className="live-watch" role="dialog" aria-modal="true" aria-label={`Watching ${hostLabel(session)}`}>
      {/* >>> **HER 2026-08-28 FINDING: THE X SAT ON TOP OF THE VIEWER COUNTER.** <<< Both were
          top-RIGHT — the bar's close button by `space-between`, and the room's count by its own
          `row-reverse` — so nobody could tap the count without closing the live. The X leads the bar
          now, on the LEFT, and the count keeps the top-right per the standing grammar. */}
      <div className="live-watch__bar">
        <button type="button" className="modal-close live-watch__close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <span className="live-watch__who"><CircleDot size={14} /> {hostLabel(session)}</span>
      </div>
      {/* The beat, then the room. A viewer whose broadcaster just ended is not left to find the X. */}
      {ended ? (
        <div className="live-ended" role="status">
          <p className="live-ended__title">The live has ended</p>
          <p className="live-ended__body">Thank you for being here. Taking you back to the Living Room.</p>
        </div>
      ) : null}
      {note ? <p className="live-watch__note">{note}</p> : null}
      {why && !note ? <p className="live-watch__note">Her stream is starting. Stay here — it will begin on its own.</p> : null}
      <div className="live-watch__stage">
        {/* >>> **IT AUTOPLAYS MUTED, WHICH IS THE ONLY WAY A PHONE LETS IT START AT ALL.** <<<
            iOS and Android both refuse `play()` on an unmuted element with no user gesture, and
            this element was unmuted — so on the one device her audience is holding, the promise was
            rejected and the picture never began. Muted autoplay is the platform's own contract, so it
            is where every viewer STARTS — `autoSound` then takes the sound off within the same beat,
            or on her next touch of anything. `muted` is set on the ELEMENT as well as in the
            attribute because React does not reflect it to the DOM property on its own. */}
        {/* `controls` is gone with the mute: a native control bar over her picture is the opposite of
            the full-screen composition she approved, and the one control it was there for is now a
            real button on the screen. */}
        <video ref={videoRef} className="live-watch__video" playsInline autoPlay muted={muted} />
        {/* >>> **THE LAST RESORT, NOT THE ROAD — HER RULING OF 2026-09-18 MADE SOUND AUTOMATIC.** <<<
            `autoSound` unmutes on arrival, and failing that on the viewer's next touch ANYWHERE, so
            in the ordinary case this button is never drawn at all: it needs `muted` to still be true.
            It stays for the browser that refuses both — a viewer who has not touched the screen once
            should still have somewhere to look. It is held to `hasAudio` because a button offering
            audio on a stream carrying none is a tap that does nothing (R35). */}
        {hasAudio && muted ? (
          <button type="button" className="live-watch__sound"
            onClick={() => {
              setMuted(false);
              const el = videoRef.current;
              if (el) { el.muted = false; el.play().catch(() => {}); }
            }}>
            <Volume2 size={16} /> Tap for sound
          </button>
        ) : null}
        <LiveRoomOverlay sessionId={session.id} title={session.title || ""} onOver={() => setEnded(true)} />
      </div>
    </div>
    </OverDock>
  );
}

/**
 * >>> THE ROOM ITSELF, OVER THE PICTURE. <<< Her clauses (a), (b) and (c) are one surface because
 * they are one moment: somebody walks in, the count moves, and the room says something about it.
 *
 * **THE BROADCASTER READS IT ON A PHONE**, which is her own note, so the comment type is sized for
 * arm's length rather than for a desktop chat panel, and the strip sits clear of the video's own
 * controls.
 */
export function LiveRoomOverlay(
  { sessionId, title, host = false, onOver }:
  { sessionId: string; title?: string; host?: boolean; onOver?: () => void },
) {
  const { viewers, comments, who, joins, over } = useLiveRoom(sessionId, true);
  // The overlay is the only thing polling, so it is the only thing that KNOWS -- it tells the screen
  // it sits on rather than drawing an ending itself, because the broadcaster's screen and a viewer's
  // screen close differently and one of them is not being closed at all.
  useEffect(() => { if (over) onOver?.(); }, [over, onOver]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [showWho, setShowWho] = useState(false);
  const [sparks, setSparks] = useState<{ id: number }[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // I2 (her answer H4-2): a viewer reports the live, or one comment, in one tap. What the server said is shown for
  // a moment, so the tap never does nothing; the reporter is never told what became of it.
  const [reportSaid, setReportSaid] = useState("");
  const [reported, setReported] = useState<Set<string>>(new Set());
  const reportTimer = useRef<number | null>(null);
  async function report(commentId?: string) {
    const key = commentId || "live";
    if (reported.has(key)) return;
    const d = await post("/api/live/report", commentId ? { sessionId, commentId } : { sessionId });
    if (d?.ok) setReported((set) => new Set(set).add(key));
    setReportSaid(String(d?.message || "That did not go through. Try again in a moment."));
    if (reportTimer.current) window.clearTimeout(reportTimer.current);
    reportTimer.current = window.setTimeout(() => setReportSaid(""), 3200);
  }
  useEffect(() => () => { if (reportTimer.current) window.clearTimeout(reportTimer.current); }, []);
  const sparkId = useRef(0);
  const laneRef = useRef<HTMLUListElement | null>(null);
  // Who I am, for the one decision this component makes about a control: my own words carry an X.
  const meRef = useRef<string>("");
  useEffect(() => {
    let alive = true;
    void json("/api/auth/session").then((d) => { if (alive && d?.member?.id) meRef.current = String(d.member.id); });
    return () => { alive = false; };
  }, []);
  // PARKED means she has dragged up to read: the lane stops following the edge until she comes back.
  const [parked, setParked] = useState(false);
  const toEdge = (behavior: ScrollBehavior = "auto") => {
    const el = laneRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setParked(false);
  };
  // **AT the bottom is a RANGE, not an equality** — sub-pixel scroll heights mean `scrollTop +
  // clientHeight === scrollHeight` is false on the very screen it is meant to be true on.
  //
  // >>> **AND THE RANGE WAS 24 PIXELS, WHICH IS NOT A RANGE A THUMB CAN LAND IN.** <<< Her event,
  // 2026-09-13: *"the comments stop scrolling up for me to see, and others who were trying to scroll
  // and see the comments can't scroll."* On a busy live the lane GROWS while she is reading it, so
  // the bottom keeps moving away from her; getting back inside a 24px window of a moving edge on a
  // phone is close to impossible, and once she misses it she is parked for the rest of the broadcast
  // and the comments appear to freeze. 96px is a thumb's worth of slack and it is the difference
  // between a lane that resumes and one that never does.
  const onLaneScroll = () => {
    const el = laneRef.current;
    if (!el) return;
    setParked(el.scrollHeight - el.scrollTop - el.clientHeight > 96);
  };

  // >>> **AND NOTHING MAY YANK THE LANE WHILE A THUMB IS ON IT.** <<< The other half of her report.
  // Comments poll every few seconds, and each arrival called `scrollTo` — which on a touch list
  // INTERRUPTS THE GESTURE IN PROGRESS. A viewer who started dragging up got snapped back to the
  // bottom on the next poll, over and over, which is exactly what "can't scroll" feels like from the
  // outside. `parked` could not save her: it is set by a scroll EVENT, and the poll routinely landed
  // between the touch starting and the first event firing.
  //
  // So the touch itself is the guard, not a state derived from it. It is released a beat after her
  // finger leaves, because momentum scrolling keeps moving after the touch ends and a jump landing
  // in that window is the same theft with better timing.
  const touchingRef = useRef(false);
  const releaseRef = useRef<number | null>(null);
  const holdLane = () => {
    touchingRef.current = true;
    if (releaseRef.current) { window.clearTimeout(releaseRef.current); releaseRef.current = null; }
  };
  const releaseLane = () => {
    if (releaseRef.current) window.clearTimeout(releaseRef.current);
    releaseRef.current = window.setTimeout(() => { touchingRef.current = false; releaseRef.current = null; }, 1200);
  };
  useEffect(() => () => { if (releaseRef.current) window.clearTimeout(releaseRef.current); }, []);

  // A new comment rides the lane to the edge, unless she is reading back — which is the whole of
  // her "a new-comment jump returning you to the live edge" clause, in the direction that never
  // steals the screen from a thumb.
  useEffect(() => { if (!parked && !touchingRef.current) toEdge(); }, [comments.length, parked]);

  async function say(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft("");
    await post("/api/live/comment", { sessionId, body });
    setSending(false);
  }

  // HER (d): the cheap dopamine. Local only and deliberately so -- a heart is a gesture, not a
  // record, and a table for them would be rows nobody ever reads (R38/R165).
  function spark() {
    const id = (sparkId.current += 1);
    setSparks((list) => [...list, { id }]);
    window.setTimeout(() => setSparks((list) => list.filter((x) => x.id !== id)), 1600);
  }

  return (
    <div className="live-room">
      <div className="live-room__top">
        {/* HER (c): the gold counter, always visible, to the broadcaster AND the viewers. */}
        <button type="button" className="live-room__count"
          onClick={() => host && setShowWho((v) => !v)}
          aria-label={`${viewers} watching`} disabled={!host}>
          <Eye size={14} /> {viewers}
        </button>
        {title ? <span className="live-room__title">{title}</span> : null}
        {!host ? (
          <button type="button" className="live-room__report" aria-label="Report this live"
            disabled={reported.has("live")} onClick={() => report()}>
            <Flag size={14} />
          </button>
        ) : null}
      </div>
      {reportSaid ? <p className="live-room__reported" role="status">{reportSaid}</p> : null}

      {/* HER (a): a line rises when somebody enters, brief, fading. */}
      <div className="live-room__joins" aria-live="polite">
        {joins.map((j) => <span key={j.id} className="live-room__join">{j.name} joined</span>)}
      </div>

      {/* HER (c) again: who is here, for the broadcaster alone -- a viewer list handed to every
          viewer is a roster of who is watching handed to anybody who taps in. */}
      {host && showWho ? (
        <ul className="live-room__who">
          {(who || []).map((w) => <li key={w.userId}>{w.name}</li>)}
          {who && who.length === 0 ? <li>Nobody yet.</li> : null}
        </ul>
      ) : null}

      {/* HER (b): comments float up the lower screen. Names are tappable, and the tap is the same
          member menu every other name in this house opens (R28/R29). */}
      {/* >>> **HER RULING 2: THE LANE IS A SCROLLER, NOT A WINDOW.** <<< New comments enter at the
          BOTTOM and the lane rides to the edge with them; drag UP and the whole history of the live
          is there, the faded ones becoming readable as they come down into the body of the mask. A
          drag PARKS the lane (nothing yanks the screen out from under a thumb), and the jump button
          below — or her own next comment — returns her to the live edge. */}
      {/* The touch guard is on the LANE itself rather than on the window: a drag anywhere else on
          the screen is not her reading the comments, and pausing the follow for it would leave the
          broadcaster staring at a stale lane while her room talks. */}
      <ul className="live-room__comments" ref={laneRef} onScroll={onLaneScroll}
        onTouchStart={holdLane} onTouchEnd={releaseLane} onTouchCancel={releaseLane}
        onPointerDown={holdLane} onPointerUp={releaseLane} onPointerCancel={releaseLane}
        onWheel={holdLane} onMouseLeave={releaseLane}>
        {comments.filter((c) => !hidden.has(c.id)).map((c) => (
          <li key={c.id} className="live-room__comment">
            {/* HER ITEM 7: the element is handed over, so the box opens under the name she tapped.
                Passing `null` put it at the fallback corner (x=24), which on a live screen is a
                menu she has to go and find while the video is still playing. */}
            {/* HER RULING 3: the picture bubble beside the name and the words, from the house's one
                Avatar — so a photo held or taken down falls back to her glyph here exactly as it
                does on every other surface (R28/R29). */}
            <Avatar who={{ ref: String(c.userId), name: c.name, avatar: c.avatar || "star", photo: c.photo || null, owner: false } as never} size="xs" />
            <button type="button" className="live-room__name"
              onClick={(e) => openMemberMenu({ ref: String(c.userId), name: c.name } as never, e.currentTarget)}>
              {c.name}
            </button>
            <span>{c.body}</span>
            {/* HER STRIKE LADDER REACHES LIVE CHAT: the host takes one down in her own room, the
                same pair every other member-written surface in this house carries. It is drawn for
                the HOST alone -- a viewer with a hide control is a viewer moderating somebody
                else's room. Hidden here the moment she taps, so the strip does not carry it for
                three more seconds while the server answers. */}
            {/* HER RULING: the host takes down anybody's; a member takes down her own. Same control,
                same meaning, and the SERVER decides which door the tap goes through. */}
            {host || String(c.userId) === String(meRef.current) ? (
              <button type="button" className="live-room__hide" aria-label={`Hide ${c.name}'s comment`}
                onClick={() => {
                  setHidden((set) => new Set(set).add(c.id));
                  post("/api/live/comment/hide", { sessionId, commentId: c.id });
                }}>
                <X size={13} />
              </button>
            ) : (
              <button type="button" className="live-room__hide" aria-label={`Report ${c.name}'s comment`}
                disabled={reported.has(c.id)} onClick={() => report(c.id)}>
                <Flag size={12} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {parked ? (
        <button type="button" className="live-room__jump" onClick={() => toEdge("smooth")}>
          Back to live
        </button>
      ) : null}

      <form className="live-room__say" onSubmit={say}>
        <input value={draft} maxLength={300} placeholder="Say something…"
          onChange={(e) => setDraft(e.target.value)} aria-label="Say something" />
        <button type="button" className="live-room__spark" onClick={spark} aria-label="Send a spark">
          <Sparkles size={18} />
        </button>
        {/* >>> **HER FIELD REPORT: COMMENTS DEAD — AND THIS FORM HAD NO WAY TO SEND ONE.** <<< Its
            only control was the spark, which is `type="button"`, so there was nothing to TAP and
            nothing to fire `onSubmit`. A phone keyboard's return key is not a substitute: on iOS it
            reads "return" and inserts nothing on a single-line input in a form with no submit
            control. A real `type="submit"` is the whole repair, and it is DISABLED on an empty
            draft so the one control she can see never answers a tap by doing nothing (R35). */}
        <button type="submit" className="live-room__send" disabled={!draft.trim() || sending}
          aria-label="Send comment">
          <Send size={18} />
        </button>
      </form>
      <div className="live-room__sparks" aria-hidden="true">
        {sparks.map((x) => <span key={x.id} className="live-room__sparkbit"><Sparkles size={16} /></span>)}
      </div>
    </div>
  );
}

/**
 * >>> GOING LIVE, IN ONE TAP. <<<
 *
 * Four states and they are four facts (R18): asking for the camera, previewing yourself, going on
 * air, and on air. A build with two would either show a member a confirm button before her camera
 * had answered, or drop her straight onto a live broadcast without ever seeing her own face.
 */
function GoLivePanel({ onClose, onLive, onEnd, isOwner = false }: {
  // HER REPORT, 2026-09-18: the gate reached a customer's screen. It is house machinery and it is
  // hers alone — a reader going live has no use for the event window and may not be shown it.
  isOwner?: boolean;
  onClose: () => void;
  onLive: (s: LiveSession, stop: () => void, stream: MediaStream | null) => void;
  onEnd: () => void;
}) {
  const previewRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const [phase, setPhase] = useState<"asking" | "preview" | "starting" | "live">("asking");
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState<string>(DEFAULT_LIVE_SCOPE);
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [groups, setGroups] = useState<{ id: string; title: string; members: number }[] | null>(null);
  const [threadId, setThreadId] = useState("");
  const [notified, setNotified] = useState(0);
  const [liveId, setLiveId] = useState("");
  const [canFlip, setCanFlip] = useState(false);
  const [error, setError] = useState("");
  // ITEM H2: true when the way out of a refused camera is the phone's own Settings page (store app only).
  const [toSettings, setToSettings] = useState(false);
  // >>> HER 2026-08-28 RULING: a swipe away PAUSES her, and only two minutes ends her. <<< The
  // decision is `livePresenceAction`'s, in src/livePresence.js, where a fixture RUNS it.
  const [presence, setPresence] = useState(LIVE_PRESENCE.LIVE);
  const awayAt = useRef(0);
  const [scopeBusy, setScopeBusy] = useState(false);
  // >>> **HER GO-LIVE SAFETY CHECK, 2026-09-13.** <<< *"she physically cannot go live with the gate
  // up by accident."* The status is re-read every time this panel opens and again after she drops
  // the gate, because a banner drawn once at mount would go stale in exactly the minutes she spends
  // choosing her audience. `null` means NOT READ, which the law reads as *cannot tell* rather than
  // as off (R18) — three states, and the unknown one is the whole reason this exists.
  const [dropStatus, setDropStatus] = useState<unknown>(null);
  const [dropRead, setDropRead] = useState(false);
  const [gateAck, setGateAck] = useState(false);
  const [dropping, setDropping] = useState(false);
  const readDrop = useCallback(() => {
      // >>> **READ THE BODY ON EVERY PATH, INCLUDING THE ONES THAT THROW IT AWAY (2026-09-18).** <<<
      // `r.ok ? r.json() : null` LEAVES A NON-OK BODY UNCONSUMED. The browser then never marks the
      // request finished, and a page waiting for the network to go quiet waits for ever — which is
      // how one 403 on this route cost fifteen render units a 60-second navigation timeout each.
      // `.json()` on a body that is not JSON rejects, and the `.catch` below is what that is for.
    void fetch("/api/event/drop/status", { cache: "no-store" })
      .then((r) => r.json().then((d) => (r.ok ? d : null)))
      .then((d) => { setDropStatus(d); setDropRead(true); })
      .catch(() => { setDropStatus(null); setDropRead(true); });
  }, []);
  // Asked only on her seat. A member's browser does not even place the request now, so the route's
  // new refusal is the second wall rather than the only one.
  useEffect(() => { if (isOwner) readDrop(); }, [readDrop, isOwner]);
  // >>> **THE GATE IS HERS ALONE.** <<< On a reader's seat this answers GATE_NOT_HERS: no banner,
  // no drop, no tick, and `mayGoLive` true — the guard's whole purpose is the OWNER not calling
  // thousands of members into a shut room, and it had been DISABLING every reader's go-live button.
  const gate = goLiveGuardFor(dropStatus, { acknowledged: gateAck, owner: isOwner });
  async function dropTheGate() {
    if (dropping) return;
    setDropping(true);
    try {
      // >>> **A WINDOW SIZED FOR *DROP NOW, TALK LATER*, NEVER THE ONE-TAP DEFAULT.** <<< Her plan
      // is to drop the gate while she gets ready, and the house default is 60 minutes starting the
      // instant she taps. Setting up for twenty-five minutes and then talking for an hour would
      // raise the gate mid-broadcast and turn away everyone who arrived late, which is this guard's
      // own failure arriving twenty minutes behind schedule.
      await fetch("/api/event/drop/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ minutes: GATE_DROP_PREP_MINUTES }),
      });
    } catch { /* the re-read below is what tells her, never this call's own optimism */ }
    // **THE BANNER IS RE-READ RATHER THAN ASSUMED.** A tap that failed must not paint a green gate:
    // the only thing allowed to turn this banner on is the server saying it is on.
    readDrop();
    setDropping(false);
  }
  // >>> **HER FILTER TOGGLE AND HER PRESET, REMEMBERED SEPARATELY (her amendment, R18).** <<< A
  // member who flips filters OFF keeps her preset for the day she flips them back, and a member who
  // never turns them on is never asked which one she wants. The default is OFF and it is the law's.
  const [filters, setFilters] = useState(() => readFilterPrefs(
    safeLocalStorage()));
  const rawRef = useRef<MediaStream | null>(null);   // the bare camera, always kept
  const pipeRef = useRef<FilterPipeline | null>(null);
  // >>> **WHICH TIER IS CARRYING HER PICTURE, POLLED OFF THE DRAW LOOP.** <<< This is the answer to
  // her question after the mirror test, and it is READ rather than inferred: a tracker that loaded
  // fine but sees no face is the full-frame tier, and a screen that claimed otherwise would send her
  // back to judge the wrong engine. Twice a second is enough for a line of text about a face.
  const [tier, setTier] = useState<FilterTier>("full-frame");
  useEffect(() => {
    if (!filters.on) { setTier("full-frame"); return undefined; }
    const t = window.setInterval(() => setTier(pipeRef.current?.tier?.() ?? "full-frame"), 500);
    return () => window.clearInterval(t);
  }, [filters.on]);

  // >>> **THE OUTGOING STREAM IS THE FILTERED ONE, OR THE BARE CAMERA — NEVER A HALF STATE.** <<<
  // Her ruling is that the audience sees the filtered her, so this is what gets published AND what
  // the preview shows: a preview that lies about the broadcast is worse than no preview.
  const attach = useCallback((s: MediaStream) => {
    rawRef.current = s;
    const preset = activeFilter(readFilterPrefs(
      safeLocalStorage()));
    pipeRef.current?.stop();
    pipeRef.current = null;
    let out = s;
    if (preset) {
      const pipe = startFilterPipeline(s, preset);
      // **NULL IS HER OFF STATE AND EVERY FAILURE ARM AT ONCE (R19).** No WebGL, no captureStream, a
      // shader that will not compile: she broadcasts bare rather than black.
      if (pipe) {
        pipeRef.current = pipe;
        out = pipe.stream;
        // THE FACE-AWARE TIER ARRIVES LATE AND IS NEVER WAITED FOR. Her camera is live in the
        // meantime on the full-frame tier, and the mask simply starts applying when it loads.
        void startFaceTracker(pipe.sourceEl).then((t) => { if (t) pipe.attachTracker(t); }).catch(() => {});
      }
    }
    streamRef.current = out;
    if (previewRef.current) previewRef.current.srcObject = out;
  }, []);

  // HER MID-LIVE TOGGLE. On air, the sender's track is REPLACED rather than re-negotiated, which is
  // the same move her camera flip makes — so turning a filter on or off mid-broadcast costs her
  // audience nothing at all.
  const applyFilters = useCallback(async (next: { on: boolean; id: string; strength: number }) => {
    setFilters(next);
    writeFilterPrefs(safeLocalStorage(), next);
    const raw = rawRef.current;
    if (!raw) return;
    const preset = activeFilter(next);
    // A preset change with the pipeline already up is one number, not a new stream.
    if (preset && pipeRef.current) { pipeRef.current.setKnobs(preset); return; }
    pipeRef.current?.stop();
    pipeRef.current = null;
    let out = raw;
    if (preset) {
      const pipe = startFilterPipeline(raw, preset);
      if (pipe) {
        pipeRef.current = pipe;
        out = pipe.stream;
        void startFaceTracker(pipe.sourceEl).then((t) => { if (t) pipe.attachTracker(t); }).catch(() => {});
      }
    }
    streamRef.current = out;
    if (previewRef.current) previewRef.current.srcObject = out;
    const sender = pcRef.current?.getSenders().find((x) => x.track?.kind === "video");
    const track = out.getVideoTracks()[0];
    if (sender && track) await sender.replaceTrack(track).catch(() => {});
  }, []);

  // The camera is asked for the moment the panel opens, because her tap WAS the request. A second
  // button before the permission prompt is a step she did not ask for.
  useEffect(() => {
    let alive = true;
    (async () => {
      // ITEM H2 (her TestFlight test, 2026-10-18): inside the app the PHONE asks first, its own camera and microphone
      // popups with the reasons in Info.plist, so the web view's request below meets a yes the phone already holds.
      const native = APP_MODE ? await import("../storeApp/native") : null;
      if (native) await native.askCameraAndMic();
      if (!alive) return;
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: true });
        if (!alive) { s.getTracks().forEach((t) => t.stop()); return; }
        attach(s);
        setPhase((p) => (p === "asking" ? "preview" : p));
        setCanFlip(await hasTwoCameras());
      } catch {
        if (!alive) return;
        // A refusal is HERS and it is not an error to apologise for -- it is a state with a way out.
        const why = cameraRefusal(APP_MODE, native ? native.phonePlatform() : "web", native ? await native.cameraAndMicNow() : null);
        if (!alive) return;
        setToSettings(why.settings);
        setError(why.text);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Her own groups, asked once. The server answers with the ones she CREATED, which is her word.
  useEffect(() => {
    let alive = true;
    json("/api/live/my-groups").then((d) => {
      if (alive) setGroups(d?.ok && Array.isArray(d.groups) ? d.groups : []);
    });
    return () => { alive = false; };
  }, []);

  // THE FLIP: a new track from the other camera, swapped INTO the live sender. Re-negotiating would
  // drop her broadcast for a second on the one act she uses mid-stream, so the sender is reused.
  async function flip() {
    const next = facing === "user" ? "environment" : "user";
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: next }, audio: true });
      const track = s.getVideoTracks()[0];
      const sender = pcRef.current?.getSenders().find((x) => x.track?.kind === "video");
      if (sender && track) await sender.replaceTrack(track);
      streamRef.current?.getVideoTracks().forEach((t) => t.stop());
      const audio = streamRef.current?.getAudioTracks() || [];
      const merged = new MediaStream([track, ...audio]);
      attach(merged);
      setFacing(next);
    } catch {
      setError("That camera could not be opened.");
    }
  }

  function teardown() {
    pcRef.current?.close();
    pcRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  async function goLive() {
    if (phase === "starting" || phase === "live") return;
    setPhase("starting");
    setError("");
    const d = await post("/api/live/start", {
      title: title.trim(), scope: liveScopeOf(scope),
      threadId: scopeNeedsGroup(scope) ? threadId : null,
    });
    if (!d?.ok) {
      setPhase("preview");
      setError(d?.message || "The broadcast could not be opened just now.");
      return;
    }
    if (d.already) { setPhase("preview"); setError("You already have a broadcast open. End it first."); return; }
    if (!d.whip?.url || !streamRef.current) {
      setPhase("preview");
      setError("The broadcast could not be opened just now.");
      return;
    }
    try {
      pcRef.current = await publishToWhip(d.whip.url, streamRef.current, d.ice);
    } catch (err) {
      // The row exists and the input is billing, so a failure to CONNECT ends it rather than
      // leaving a broadcast nobody is watching and nobody is in.
      await post("/api/live/end", { sessionId: d.session?.id });
      setPhase("preview");
      setError(err instanceof Error ? err.message : "The broadcast could not connect just now.");
      return;
    }
    // Her seat only: the OBS door, behind a link, never opened for a member.
    // HER CLAUSE 3: she sees the call went out, read off what was really written.
    setNotified(Number(d.notified || 0));
    setLiveId(String(d.session?.id || ""));
    setPhase("live");
    onLive(d.session, teardown, streamRef.current);
  }


  // >>> **THE SWIPE, THE PHONE CALL, AND THE TWO MINUTES.** <<< One effect, armed only while she is
  // really on air. `visibilitychange` is what a phone fires when she swipes away, takes a call, or
  // locks the screen -- and it fires again the moment she comes back, which is the half her sentence
  // is really about ("the broadcast should resume when they come back").
  useEffect(() => {
    if (phase !== "live") return undefined;
    let timer = 0;
    const settle = () => {
      const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
      if (hidden && !awayAt.current) awayAt.current = Date.now();
      if (!hidden) awayAt.current = 0;
      const awayMs = awayAt.current ? Date.now() - awayAt.current : 0;
      const next = livePresenceAction({ hidden, awayMs });
      setPresence(next);
      // HER CLAUSE, BOTH TRACKS: `enabled` rather than `stop()`, because a stopped track cannot be
      // restarted -- resuming would need a fresh `getUserMedia` and a new permission prompt on the
      // phone call she is already annoyed by. Disabled tracks resume instantly and the
      // PeerConnection never notices they went quiet.
      const send = tracksShouldSend(next);
      streamRef.current?.getTracks().forEach((t) => { t.enabled = send; });
      if (next === LIVE_PRESENCE.ENDED) onEnd();
    };
    const onVis = () => { settle(); };
    document.addEventListener("visibilitychange", onVis);
    // The clock has to keep asking while she is AWAY, or two minutes of a locked screen would only
    // be noticed when she came back -- which is the one moment it must not end her broadcast.
    timer = window.setInterval(settle, 5000);
    settle();
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.clearInterval(timer);
      // Never leave her muted: whatever this effect disabled, it re-enables on the way out.
      streamRef.current?.getTracks().forEach((t) => { t.enabled = true; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // HER IN-LIVE DOOR CHANGE. The arithmetic is the SERVER'S -- only newly-added audiences are called,
  // and nobody already invited is buzzed twice -- so this posts the intent and draws the answer.
  async function changeScope(next: string, groupId: string) {
    if (scopeBusy) return;
    setScopeBusy(true);
    const d = await post("/api/live/scope", { sessionId: liveId, scope: next, threadId: groupId || null });
    if (d?.ok) {
      setScope(next);
      if (groupId) setThreadId(groupId);
      if (typeof d.notified === "number") setNotified((n) => n + d.notified);
    }
    setScopeBusy(false);
  }

  if (phase === "live" && liveId) {
    return (
      <>
        <LiveSelfView
          stream={streamRef.current}
          session={{ id: liveId, title, scope }}
          onEnd={onEnd}
          onFlip={flip}
          canFlip={canFlip}
          scope={scopeNeedsGroup(scope) && threadId ? `group:${threadId}` : scope}
          groups={groups}
          onScope={changeScope}
          scopeBusy={scopeBusy}
        />
        {/* HER PAUSED SCREEN. Over her own picture, so she can see she is still holding the room. */}
        {showsPausedCard(presence) ? (
          <div className="live-paused" role="status">
            <p className="live-paused__title">Paused</p>
            <p className="live-paused__body">
              Come back to this screen and you are live again. It ends on its own after two minutes away.
            </p>
          </div>
        ) : null}
      </>
    );
  }

  if (error && phase === "asking") {
    return (
      <div className="live-start">
        <h3>Go live</h3>
        <p className="live-error">{error}</p>
        {toSettings ? (
          <button type="button" className="live-start__settings"
            onClick={() => { void import("../storeApp/native").then((n) => n.openPhoneSettings()); }}>
            Open Settings
          </button>
        ) : null}
        <button type="button" className="lr-link" onClick={() => { teardown(); onClose(); }}>Close</button>
      </div>
    );
  }

  return (
    <div className="live-start">
      <h3>{phase === "live" ? "You are on air" : "Go live"}</h3>
      <div className="live-preview">
        <video ref={previewRef} className="live-preview__video" autoPlay playsInline muted />
        {phase === "asking" ? <p className="live-preview__note"><Loader size={16} /> Allow your camera and microphone…</p> : null}
        {phase === "live" ? <span className="live-preview__onair"><CircleDot size={12} /> LIVE</span> : null}
        {phase === "live" && liveId ? <LiveRoomOverlay sessionId={liveId} host /> : null}
        {canFlip ? (
          <button type="button" className="live-preview__flip" onClick={flip} aria-label="Switch camera">
            <SwitchCamera size={18} />
          </button>
        ) : null}
      </div>

      {phase === "preview" || phase === "starting" ? (
        <>
          <label className="live-field">
            <span>What is this about?</span>
            <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="Optional" />
          </label>
          {/* >>> HER AUDIENCE PICKER, REBUILT. <<< The old one was two oversized boxes with the
              labels invented at this call site; hers are declared in `src/liveRoom.js` so the
              picker, the notification and the refusal sentence all say the same words (R28/R29).
              One row per option, aligned, each the whole row a thumb can land on. */}
          {/* >>> HER RULING: NO NAKED BROWSER CONTROLS, EVER, ANYWHERE. <<< The white circle with
              the blue dot is the browser's, not this house's, and it was the loudest thing on the
              one screen a member opens to put her face on the site.

              **THE RADIO IS GONE, NOT HIDDEN BEHIND A LABEL.** These are BUTTONS carrying
              `role="radio"`, so the keyboard and a screen reader get the same group the native
              control gave them -- what is removed is the browser's paint, never the semantics. A
              `.sr-only` input would have left the thing she is pointing at on the page.

              Identical markup at both viewports, because her clause 1 is that the phone has the
              SAME capability: one column of full-width cards, thumb-sized, and the stylesheet does
              the rest rather than a second tree somebody has to keep in step. */}
          {/* >>> **HER GO-LIVE SAFETY CHECK. BIG, FIRST, AND ABOVE THE AUDIENCE PICKER.** <<<
              It sits here rather than beside the button on purpose: the picker is where she decides
              WHO gets called, and the gate is what decides whether those people can actually get in.
              Reading them in that order is the whole point.

              On 2026-09-13 the house called 3,384 members with this gate up and every viewer who got
              in was a paying vet. Nothing was broken. Nothing on her screen said so either. */}
          {isOwner && dropRead ? (
            <div className={`gate-banner is-${gate.banner}`} role="status" aria-live="polite">
              <p className="gate-banner__head">{gate.text}</p>
              <p className="gate-banner__sub">{gate.sub}</p>
              {gate.offerDrop || gate.offerExtend ? (
                <button type="button" className="gate-banner__drop" onClick={dropTheGate} disabled={dropping}>
                  {dropping ? "Dropping…" : (gate.offerExtend ? "Give it longer" : "Drop the gate now")}
                </button>
              ) : null}
              {gate.needsAcknowledgement ? (
                <label className="gate-banner__ack">
                  <input type="checkbox" checked={gateAck} onChange={(e) => setGateAck(e.target.checked)} />
                  <span>Go live anyway, with the gate up</span>
                </label>
              ) : null}
            </div>
          ) : null}

          <div className="live-pick" role="radiogroup" aria-label="Who can watch — and who gets called">
            <p className="live-pick__legend">Who can watch — and who gets called</p>
            {LIVE_SCOPES.map((key: string) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={scope === key}
                className={scope === key ? "live-card is-on" : "live-card"}
                onClick={() => setScope(key)}
              >
                <span className="live-card__mark" aria-hidden="true" />
                <span className="live-card__label">{LIVE_SCOPE_LABELS[key as keyof typeof LIVE_SCOPE_LABELS]}</span>
              </button>
            ))}
          </div>

          {/* HER THIRD OPTION: the groups SHE created, never the ones she was added to. The list is
              the server's answer -- a group she does not own is refused at the start door too. */}
          {scopeNeedsGroup(scope) ? (
            groups === null ? <p className="live-note">Looking for your groups…</p>
            : groups.length === 0 ? <p className="live-note">You have not created a group yet.</p>
            : (
              <div className="live-pick" role="radiogroup" aria-label="Which group">
                <p className="live-pick__legend">Which group</p>
                {groups.map((grp) => (
                  <button
                    key={grp.id}
                    type="button"
                    role="radio"
                    aria-checked={threadId === grp.id}
                    className={threadId === grp.id ? "live-card is-on" : "live-card"}
                    onClick={() => setThreadId(grp.id)}
                  >
                    <span className="live-card__mark" aria-hidden="true" />
                    <span className="live-card__label">{grp.title}</span>
                    <span className="live-card__count">{grp.members} in it</span>
                  </button>
                ))}
              </div>
            )
          ) : null}
        </>
      ) : null}

      {/* >>> **HER FILTER RAIL. THE TOGGLE FIRST, AND IT IS OFF UNTIL SHE SAYS OTHERWISE.** <<<
          Her amendment: *"filters are OPTIONAL and OFF BY DEFAULT ... a member who never wants
          filters never sees the carousel again unless they flip it."* So the switch is always
          drawn and the carousel is not — and the switch reads its state from what she chose last
          time, not from a default this component invented. */}
      <div className="live-filters">
        <button
          type="button"
          className={`live-filters__toggle${filters.on ? " is-on" : ""}`}
          role="switch"
          aria-checked={filters.on}
          onClick={() => { void applyFilters({ ...filters, on: !filters.on }); }}
        >
          <span className="live-filters__knob" aria-hidden="true" />
          <span className="live-filters__label">Filters</span>
          <span className="live-filters__state">{filters.on ? "On" : "Off"}</span>
        </button>
        {filters.on ? (
          <>
            <div className="live-filters__rail" role="radiogroup" aria-label="Filter">
              {LIVE_FILTERS.map((p: { id: string; name: string }) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={filters.id === p.id}
                  className={`live-filters__pick${filters.id === p.id ? " is-on" : ""}`}
                  onClick={() => { void applyFilters({ ...filters, on: true, id: p.id }); }}
                >
                  {p.name}
                </button>
              ))}
            </div>
            {/* >>> **HER INTENSITY SLIDER.** <<< Her ruling after the mirror test: *"adjustable
                intensity slider so each broadcaster tunes their own."* It moves the smoothing only,
                live, with no new track — so she can drag it while looking at her own face and stop
                where it flatters her rather than where a preset guessed. */}
            <label className="live-filters__strength">
              <span className="live-filters__strength-label">Smoothing</span>
              <input
                type="range"
                min={STRENGTH_MIN}
                max={STRENGTH_MAX}
                step={0.05}
                value={filters.strength}
                aria-label="Smoothing strength"
                onChange={(e) => { void applyFilters({ ...filters, strength: Number(e.target.value) }); }}
              />
              <span className="live-filters__strength-value">{Math.round(filters.strength * 100)}%</span>
            </label>
            {/* >>> **WHICH TIER IS ACTUALLY RUNNING, SAID ON THE SCREEN.** <<< Her question after
                the mirror test was *"was I getting the fallback instead of the face tier?"* and
                nothing could answer it. This reads it off the draw loop, so her re-test tells her
                which engine she is judging instead of leaving it to be guessed at afterwards. */}
            <p className="live-filters__tier">
              {tier === "face"
                ? "Face detected — smoothing your skin, keeping eyes and lips sharp."
                : "No face detected — smoothing the whole picture."}
            </p>
          </>
        ) : null}
      </div>

      {error ? <p className="live-error">{error}</p> : null}
      {phase === "live" ? (
        <p className="live-note">
          {notified > 0 ? `Notified ${notified} ${notified === 1 ? "person" : "people"}. ` : ""}
          Nothing is recorded. When you end it, it is gone — there are no replays.
        </p>
      ) : null}

      <div className="live-start__bar">
        {/* >>> **"HIDE" MUST HIDE, AND UNTIL NOW IT ENDED HER BROADCAST WITHOUT TELLING ANYBODY.** <<<
            This handler was `{ teardown(); onClose(); }` in BOTH states: on air, tapping a control
            that says *Hide* closed the PeerConnection and stopped every camera track, while nothing
            called `/api/live/end` — so her circle stayed up in the rail, her audience tapped into a
            stream nobody was publishing (a black screen with the room and the comments still
            working, which is exactly what she reported), and she had no self-view left because the
            only surface carrying it had just closed.
            Teardown belongs to CANCEL, which is before she is ever on air, and to END, which tells
            the server first. Hiding is a view change and nothing else (R18). */}
        <button type="button" className="lr-link"
          onClick={() => { if (phase !== "live") teardown(); onClose(); }}>
          {phase === "live" ? "Hide" : "Cancel"}
        </button>
        {/* **THE BUTTON ITSELF CHANGES ITS WORDS WHEN THE GATE IS UP**, and that is the guard
            rather than a decoration: a button reading the same in both states is the button she
            taps without looking, which is exactly how 3,384 people were called into a shut room.
            `mayGoLive` is the law's answer, never a second rule spelled out here (R28/R29). */}
        {phase !== "live" ? (
          <button type="button" className={gate.warn ? "lr-share is-warned" : "lr-share"}
            onClick={goLive}
            disabled={phase !== "preview" || (scopeNeedsGroup(scope) && !threadId) || !gate.mayGoLive}>
            <Radio size={14} />{goLiveButtonLabel(gate, { starting: phase === "starting" })}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * >>> **HER OWN FACE, WHILE SHE IS ON AIR — THE SURFACE THAT OUTLIVES THE SHEET.** <<<
 *
 * Her order: *"her own feed, mirrored, full-bleed behind the overlays, unaffected by the
 * viewer-player's polling/muting medicine."*
 *
 * **IT IS A `MediaStream` AND NEVER HLS, WHICH IS THE WHOLE OF THAT LAST CLAUSE.** `LiveWatch`
 * polls `/api/live/watch` because Cloudflare needs ten to twenty seconds to have a playback URL,
 * and it autoplays MUTED because a phone refuses to start an unmuted element without a gesture.
 * Neither applies to her: this is the camera she is already holding, attached directly, with no
 * round trip to ask whether it exists. **It is muted for a different reason and the reason matters
 * (R18): her own microphone played back into her own speaker is a feedback howl.** So the word is
 * the same and the law is not, and neither one is inherited from the viewer's player.
 *
 * **MIRRORED, because a self-view that is not mirrored is the one thing every person alive reads as
 * broken** — it is her reflection, not her broadcast, and the transform is on THIS element only, so
 * nothing her audience sees is flipped.
 */
/**
 * >>> **HER OWN SCREEN WHILE SHE IS ON AIR.** <<<
 *
 * Rendered by `GoLivePanel` rather than by the rail, and that is a ruling rather than a tidy-up: the
 * camera and the PeerConnection are the panel's, so a screen that offers a CAMERA FLIP has to be
 * inside the component that owns the sender it swaps the track into. Rendering it a level up meant
 * lifting a MediaStream, a pc ref and a facing flag out of the one place that already had them.
 */
function LiveSelfView(
  { stream, session, onEnd, onFlip, canFlip, scope, groups, onScope, scopeBusy }:
  {
    stream: MediaStream | null;
    session: LiveSession;
    onEnd: () => void;
    onFlip: () => void;
    canFlip: boolean;
    scope: string;
    groups: { id: string; title: string; members: number }[] | null;
    onScope: (next: string, threadId: string) => void;
    scopeBusy: boolean;
  },
) {
  const ref = useRef<HTMLVideoElement | null>(null);
  // Attached in an EFFECT rather than at the call site, because `srcObject` is a DOM property that
  // no attribute can carry — a JSX `srcObject={...}` is silently dropped and the element stays black.
  useEffect(() => {
    const el = ref.current;
    if (!el || !stream) return;
    el.srcObject = stream;
    el.play().catch(() => {});
  }, [stream]);

  return (
    <div className="live-self" role="dialog" aria-label="You are on air">
      <video ref={ref} className="live-self__video" autoPlay playsInline muted />
      <div className="live-self__bar">
        <span className="live-self__onair"><CircleDot size={12} /> LIVE</span>
        {/* >>> **HER 2026-08-28 RULING: THE SETTINGS BUTTON DIES.** <<< It reopened the audience
            picker OVER her broadcast with no way back in — she was on air and looking at a form.
            What replaces it is the one control a phone broadcaster actually reaches for. */}
        {canFlip ? (
          <button type="button" className="live-self__flip" onClick={onFlip} aria-label="Switch camera">
            <SwitchCamera size={18} />
          </button>
        ) : null}
        <button type="button" className="live-self__end" onClick={onEnd}>End live</button>
      </div>
      {/* >>> **AND THE ONLY REAL SETTING COMES WITH HER: WHO CAN WATCH, CHANGED WITHOUT LEAVING.**
          <<< Her ruling. It is a `<select>` rather than a second sheet, because a sheet is the thing
          that took her off her own broadcast. `disabled` while the change is in flight, so a second
          tap cannot re-run the notification arithmetic. */}
      <label className="live-self__door">
        <span className="live-self__door-label">Who can watch</span>
        <select
          className="live-self__door-pick"
          value={scope}
          disabled={scopeBusy}
          onChange={(e) => {
            const next = e.target.value;
            if (next.startsWith("group:")) onScope("group", next.slice(6));
            else onScope(next, "");
          }}
        >
          {LIVE_SCOPES.filter((k: string) => k !== "group").map((k: string) => (
            <option key={k} value={k}>{LIVE_SCOPE_LABELS[k]?.title || k}</option>
          ))}
          {(groups || []).map((g) => (
            <option key={g.id} value={`group:${g.id}`}>{g.title}</option>
          ))}
        </select>
      </label>
      <LiveRoomOverlay sessionId={session.id} title={session.title || ""} host />
    </div>
  );
}

// THE RAIL — her live circles, at the top of the Living Room.
export function LiveRail({ canGoLive, isOwner = false }: { canGoLive: boolean; isOwner?: boolean }) {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [opening, setOpening] = useState(false);
  const [watching, setWatching] = useState<LiveSession | null>(null);
  const [mine, setMine] = useState<LiveSession | null>(null);
  const stopRef = useRef<null | (() => void)>(null);
  const [liveStream, setLiveStream] = useState<MediaStream | null>(null);

  const load = useCallback(async () => {
    const d = await json("/api/live/sessions");
    if (!d?.ok || !Array.isArray(d.sessions)) return;
    const now = Date.now();
    setSessions(d.sessions.filter((s: LiveSession) => sessionIsWatchable(s, now)));
    setMine(d.sessions.find((s: LiveSession) => s.mine) || null);
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, LIVE_HEARTBEAT_MS);
    return () => window.clearInterval(t);
  }, [load]);

  // >>> **HER CLAUSE 2: THE TAP LANDS IN THE LIVE.** <<< The fan-out has composed
  // `/living-room?live=<id>` since the day it shipped and nothing ever read it, so her audience
  // arrived in a feed to go hunting through (R38/R165). The decision is `src/liveLink.js`'s, where a
  // fixture RUNS it; this only acts on the answer.
  //
  // **IT RUNS EVEN WHEN THE RAIL DRAWS NOTHING**, which is the signed-out half of her clause: a
  // stranger tapping the push mounts this component, meets the sign-in door, and the intent is
  // already stashed — so the sign-in she completes lands her in the live rather than back where she
  // started. The stash is spent the moment it is used.
  useEffect(() => {
    const store = safeSessionStorage();
    const search = typeof window === "undefined" ? "" : window.location.search;
    const wanted = liveToOpen({ search, stashed: readLiveIntent(store) });
    if (!wanted) return;
    // The parameter leaves the address whatever happens next, so a refresh cannot re-open a live she
    // has already left (the funnel bridge's own recorded reason, one door over).
    const clean = addressWithoutLive(search, window.location.pathname);
    if (clean && clean !== window.location.pathname + search) window.history.replaceState({}, "", clean);
    const found = sessions.find((x) => String(x.id) === String(wanted));
    if (!found) {
      // Not here YET — the rail may not have loaded, or she may still be signing in. Hold the intent
      // rather than dropping it, which is the whole of the signed-out half.
      writeLiveIntent(store, wanted);
      return;
    }
    clearLiveIntent(store);
    if (found.mine) return;   // her own broadcast opens on her own screen, not in the watch overlay
    setWatching(found);
  }, [sessions]);

  // Without this her broadcast goes stale in 90s and the room stops drawing it while she is talking.
  useEffect(() => {
    if (!mine?.id) return undefined;
    const beat = () => { post("/api/live/heartbeat", { sessionId: mine.id }); };
    beat();
    const t = window.setInterval(beat, LIVE_HEARTBEAT_MS);
    return () => window.clearInterval(t);
  }, [mine?.id]);

  async function endMine() {
    if (!mine?.id) return;
    await post("/api/live/end", { sessionId: mine.id });
    stopRef.current?.();
    stopRef.current = null;
    setMine(null);
    setOpening(false);
    setLiveStream(null);
    load();
  }

  if (!canGoLive && sessions.length === 0) return null;

  return (
    <div className="live-rail">
      <div className="live-rail__circles">
        {canGoLive && !mine ? (
          <button type="button" className="live-circle live-circle--go" onClick={() => setOpening(true)}>
            <span className="live-circle__ring"><Radio size={18} /></span>
            <span className="live-circle__name">Go live</span>
          </button>
        ) : null}
        {canGoLive && mine ? (
          <button type="button" className="live-circle live-circle--mine" onClick={endMine}>
            <span className="live-circle__ring is-live">{initialOf(mine)}</span>
            <span className="live-circle__name">End</span>
          </button>
        ) : null}
        {/* >>> **HER 2026-08-28 RULING 4: THE CIRCLE IS THE BROADCASTER'S FACE, RINGED LIVE.** <<<
            Her own picture through the house's ONE Avatar (so a photo she takes down falls back to
            her glyph here as everywhere), inside a green ring with a LIVE tag under it. The green is
            the ONE colour this house lets past black and gold and it is earned: her order is that
            somebody on air must be unmistakable at a glance, and gold is what every other circle in
            the rail already wears. */}
        {sessions.filter((s) => !s.mine).map((s) => (
          <button key={s.id} type="button" className="live-circle live-circle--onair" onClick={() => setWatching(s)}>
            <span className="live-circle__ring is-live is-onair">
              <Avatar who={{ ref: String(s.userId || ""), name: hostLabel(s), avatar: "star", photo: s.photo || null, owner: false } as never} size="md" />
              <span className="live-circle__tag">LIVE</span>
            </span>
            <span className="live-circle__name">{hostLabel(s)}</span>
          </button>
        ))}
      </div>
      {/* >>> **THE PANEL STAYS MOUNTED WHILE SHE IS ON AIR, AND IT DRAWS THE LIVE SCREEN ITSELF.**
          <<< Her 2026-08-28 ruling puts a CAMERA FLIP on that screen, and a flip swaps a track into
          the RTCRtpSender this component made — so the screen has to live inside the component that
          owns it. Drawing it a level up meant lifting a stream, a pc ref and a facing flag out of
          the one place that already had all three (R28/R29). */}
      {opening ? (
        <OverDock>
          <GoLivePanel
            isOwner={isOwner}
            onClose={() => { setOpening(false); load(); }}
            onLive={(s, stop, stream) => { stopRef.current = stop; setMine(s); setLiveStream(stream); load(); }}
            onEnd={endMine}
          />
        </OverDock>
      ) : null}
      {/* >>> HER CLOSURE LAW (c): THE CIRCLE GOES WITH THE BROADCAST. <<< Leaving a watch screen
          refreshes the rail in the same breath, so a viewer who just watched a live end never turns
          round to a circle offering the room she was just told is over. Everyone else loses it on
          the next heartbeat, which is 20s -- stated rather than implied (R17/R160). */}
      {watching ? <LiveWatch session={watching} onClose={() => { setWatching(null); load(); }} /> : null}
    </div>
  );
}

// HER KILL SWITCH — the owner arm of the moderation rail. `mayEndSession` and the store's WHERE
// clause decide who may end what; nothing here does.
export function LiveKillSwitch() {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const d = await json("/api/live/sessions");
    if (d?.ok && Array.isArray(d.sessions)) setSessions(d.sessions);
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, LIVE_HEARTBEAT_MS);
    return () => window.clearInterval(t);
  }, [load]);

  async function end(s: LiveSession) {
    const d = await post("/api/live/end", { sessionId: s.id });
    setNote(d?.ok ? `Ended ${hostLabel(s)}.` : (d?.message || "That broadcast could not be ended."));
    load();
  }

  // ITEM 6b (her order, 2026-10-01): "Nobody is live." was one of the messages at the top of her
  // dashboard she does not want. The switch draws ONLY when there is a broadcast to end (or the note
  // from one she just ended); the rail itself still shows who is live everywhere else.
  if (sessions.length === 0 && !note) return null;

  return (
    <section className="live-kill">
      <h3>Live now</h3>
      {note ? <p className="live-note">{note}</p> : null}
      <ul className="live-kill__list">
        {sessions.map((s) => (
          <li key={s.id}>
            <span>{hostLabel(s)}{s.title ? ` — ${s.title}` : ""}</span>
            <button type="button" className="lr-link" onClick={() => end(s)}>End this broadcast</button>
          </li>
        ))}
      </ul>
    </section>
  );
}
